import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { useMutation, useQuery } from '@tanstack/react-query'
import { BaseDialog } from '../../../../components/modals/BaseDialog'
import ConfirmationModal from '../../../../components/modals/ConfirmationModal'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import useNotification from '../../../../hooks/useNotification'

const CANCELED_SERVICE_ORDER_STATUS = 4
const SCAN_DEBOUNCE_MS = 800

const normalizeCode = (value) => String(value ?? '').trim()

const codesMatch = (firstCode, secondCode) => {
  const first = normalizeCode(firstCode)
  const second = normalizeCode(secondCode)

  if (!first || !second) return false
  if (first === second) return true

  if (/^\d+$/.test(first) && /^\d+$/.test(second)) {
    return Number(first) === Number(second)
  }

  return false
}

const getActiveExtraEquipments = (item) =>
  (item?.extra_equipments ?? item?.extraEquipments ?? []).filter(
    (extra) => !extra?.pivot?.deleted_at,
  )

const getExtraRequestedQuantity = (item) =>
  getActiveExtraEquipments(item).reduce(
    (total, extra) => total + (Number(extra?.pivot?.quantity) || 0),
    0,
  )

const getExtraPendingQuantity = (item) =>
  getActiveExtraEquipments(item).reduce(
    (total, extra) => total + (Number(extra?.pivot?.fulfilled_quantity) || 0),
    0,
  )

const getRequestedQuantity = (item) =>
  (Number(item?.quantity) || 0) +
  (Number(item?.quantity_product) || 0) +
  getExtraRequestedQuantity(item)

const getPendingQuantity = (item) =>
  (Number(item?.fulfilled_quantity) || 0) +
  (Number(item?.fulfilled_quantity_product) || 0) +
  getExtraPendingQuantity(item)

const mapVolumeItemToPayload = (item) => ({
  type: item.product_item_id != null ? 'product' : 'equipment',
  movement_item_id: item.movement_item_id,
  ...(item.product_item_id != null && {
    product_item_id: item.product_item_id,
  }),
  ...(item.equipment_id != null && {
    equipment_id: item.equipment_id,
  }),
  quantity: item.quantity,
})

const SummaryItem = ({ label, value, children }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    {children ?? (
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    )}
  </Box>
)

/**
 * Modal aberto ao bipar um volume na validação do carregamento.
 *
 * Mostra os equipamentos do volume para uma nova bipagem e o resumo da
 * requisição. Ao validar sem bipar todos os equipamentos, os itens restantes
 * são removidos apenas do volume e permanecem no movimento de origem. Quando o
 * carregamento é validado, eles são agrupados no volume
 * "Pendente de Carregamento - {id do carregamento}".
 */
const VolumeValidationModal = ({ open, volume, onClose, onValidated }) => {
  const { get: baseServiceGet, post: baseServicePost } = useBaseService()
  const { showNotification } = useNotification()

  const scanInputRef = useRef(null)
  const [scanValue, setScanValue] = useState('')
  const [scannedKeys, setScannedKeys] = useState([])
  const [confirmPartialOpen, setConfirmPartialOpen] = useState(false)

  const volumeId = volume?.id
  const movementId = volume?.movement_id

  const {
    data: volumeItems = [],
    isLoading: isLoadingVolumeItems,
    isError: isVolumeItemsError,
    refetch: refetchVolumeItems,
  } = useQuery({
    queryKey: ['volumeValidationItems', movementId, volumeId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.movements.volumeItems(movementId, volumeId),
        { filters: {}, page: 1, view: 'display' },
      )

      return response.result?.data ?? []
    },
    enabled: open && !!movementId && !!volumeId,
    staleTime: 0,
  })

  const {
    data: movement,
    isLoading: isLoadingMovement,
    isError: isMovementError,
  } = useQuery({
    queryKey: ['movementDetails', movementId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.movements.getById(movementId),
      )
      return response.result
    },
    enabled: open && !!movementId,
    staleTime: 5 * 60 * 1000,
  })

  const validateItemsMutation = useMutation({
    mutationFn: async (payload) =>
      baseServicePost(entities.volumes.validateItems(volumeId), payload, {
        skipNotification: true,
      }),
  })

  const equipmentRows = useMemo(
    () =>
      volumeItems
        .filter((item) => item.equipment_id != null)
        .sort((first, second) =>
          normalizeCode(first.equipment?.number).localeCompare(
            normalizeCode(second.equipment?.number),
          ),
        )
        .map((item) => ({
          key: String(item.id),
          volumeItemId: item.id,
          equipmentNumber: item.equipment?.number ?? '-',
          equipmentName: item.equipment?.name ?? '-',
        })),
    [volumeItems],
  )

  const productItems = useMemo(
    () => volumeItems.filter((item) => item.product_item_id != null),
    [volumeItems],
  )
  const productItemsQuantity = useMemo(
    () =>
      productItems.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0,
      ),
    [productItems],
  )

  const scannedKeySet = useMemo(() => new Set(scannedKeys), [scannedKeys])
  const unscannedRows = useMemo(
    () => equipmentRows.filter((row) => !scannedKeySet.has(row.key)),
    [equipmentRows, scannedKeySet],
  )
  const scannedCount = equipmentRows.length - unscannedRows.length

  const serviceOrder = movement?.service_order
  const orderItems = useMemo(
    () =>
      (serviceOrder?.items ?? []).filter(
        (item) =>
          !item.deleted_at &&
          Number(item.status_id) !== CANCELED_SERVICE_ORDER_STATUS,
      ),
    [serviceOrder?.items],
  )

  const requestedQuantity = useMemo(
    () => orderItems.reduce((total, item) => total + getRequestedQuantity(item), 0),
    [orderItems],
  )
  const pendingQuantity = useMemo(
    () => orderItems.reduce((total, item) => total + getPendingQuantity(item), 0),
    [orderItems],
  )

  const hasOrderItems = orderItems.length > 0 && requestedQuantity > 0
  const hasMovementSummary = !!serviceOrder && !isMovementError
  const isOrderComplete = hasOrderItems && pendingQuantity === 0
  const orderStatusLabel = isLoadingMovement
    ? 'Carregando...'
    : !hasMovementSummary
      ? '-'
      : !hasOrderItems
        ? 'Sem itens'
        : isOrderComplete
          ? 'Completa'
          : 'Pendente'
  const orderStatusColor = !hasOrderItems
    ? 'default'
    : isOrderComplete
      ? 'success'
      : 'warning'

  const focusScanInput = useCallback(() => {
    setTimeout(() => scanInputRef.current?.focus(), 0)
  }, [])

  useEffect(() => {
    if (!open) return

    setScanValue('')
    setScannedKeys([])
    setConfirmPartialOpen(false)
  }, [open, volumeId])

  useEffect(() => {
    if (!open || isLoadingVolumeItems || isVolumeItemsError) return

    const timer = setTimeout(() => scanInputRef.current?.focus(), 150)
    return () => clearTimeout(timer)
  }, [
    open,
    isLoadingVolumeItems,
    isVolumeItemsError,
    equipmentRows.length,
  ])

  const processScan = useCallback(
    (rawValue) => {
      if (!open || isLoadingVolumeItems || isVolumeItemsError) return
      if (validateItemsMutation.isPending || confirmPartialOpen) return

      const code = normalizeCode(rawValue)
      if (!code) return

      const matchingRow = equipmentRows.find(
        (row) =>
          codesMatch(row.equipmentNumber, code) && !scannedKeySet.has(row.key),
      )

      if (!matchingRow) {
        const scannedRow = equipmentRows.find((row) =>
          codesMatch(row.equipmentNumber, code),
        )

        showNotification(
          'warning',
          scannedRow
            ? 'Este equipamento já foi bipado.'
            : 'Equipamento não pertence a este volume.',
        )
        setScanValue('')
        focusScanInput()
        return
      }

      setScannedKeys((previous) => [...previous, matchingRow.key])
      setScanValue('')
      focusScanInput()
    },
    [
      confirmPartialOpen,
      equipmentRows,
      focusScanInput,
      isLoadingVolumeItems,
      isVolumeItemsError,
      open,
      scannedKeySet,
      showNotification,
      validateItemsMutation.isPending,
    ],
  )

  const handleScanKeyDown = (event) => {
    if (event.key !== 'Enter') return

    event.preventDefault()
    if (scanValue.trim() === '') return

    processScan(scanValue)
  }

  const handleRemoveScan = (key) => {
    if (validateItemsMutation.isPending || confirmPartialOpen) return

    setScannedKeys((previous) => previous.filter((item) => item !== key))
    focusScanInput()
  }

  useEffect(() => {
    if (!open || scanValue.trim() === '') return
    if (isLoadingVolumeItems || isVolumeItemsError) return
    if (validateItemsMutation.isPending || confirmPartialOpen) return

    const timer = setTimeout(
      () => processScan(scanValue),
      SCAN_DEBOUNCE_MS,
    )

    return () => clearTimeout(timer)
  }, [
    confirmPartialOpen,
    isLoadingVolumeItems,
    isVolumeItemsError,
    open,
    processScan,
    scanValue,
    validateItemsMutation.isPending,
  ])

  const addValidatedVolume = useCallback(
    (items, removedCount = 0) => {
      if (!items.length) {
        onValidated(volume, [])
        showNotification(
          'warning',
          'Nenhum item restou neste volume após a validação. O volume não foi adicionado ao carregamento.',
        )
        onClose()
        return
      }

      onValidated(volume, items.map(mapVolumeItemToPayload))

      if (removedCount > 0) {
        showNotification(
          'success',
          `${removedCount} ${removedCount === 1 ? 'item não bipado permaneceu' : 'itens não bipados permaneceram'} no movimento de origem e ${removedCount === 1 ? 'será agrupado' : 'serão agrupados'} no volume de pendências quando o carregamento for validado.`,
        )
      }

      onClose()
    },
    [onClose, onValidated, showNotification, volume],
  )

  const handleValidateVolume = useCallback(
    async ({ removeUnscanned = false } = {}) => {
      if (!volume || validateItemsMutation.isPending) return

      if (!removeUnscanned) {
        addValidatedVolume(volumeItems)
        return
      }

      const scannedVolumeItemIds = equipmentRows
        .filter((row) => scannedKeySet.has(row.key))
        .map((row) => row.volumeItemId)

      try {
        const response = await validateItemsMutation.mutateAsync({
          scanned_volume_item_ids: scannedVolumeItemIds,
        })

        if (!response?.result) {
          focusScanInput()
          return
        }

        const remainingItems = response.result.remaining_items
        const fallbackRemainingItems = volumeItems.filter(
          (item) =>
            item.product_item_id != null ||
            scannedVolumeItemIds.includes(item.id),
        )

        const itemsToAdd =
          Array.isArray(remainingItems) && remainingItems.length > 0
            ? remainingItems
            : fallbackRemainingItems

        addValidatedVolume(
          itemsToAdd,
          Number(response?.result?.removed_count) || 0,
        )
      } catch (error) {
        console.error('Erro ao validar os itens do volume:', {
          status: error?.status,
          message: error?.message,
        })
        focusScanInput()
      }
    },
    [
      addValidatedVolume,
      equipmentRows,
      focusScanInput,
      scannedKeySet,
      validateItemsMutation,
      volume,
      volumeItems,
    ],
  )

  const handleValidateClick = () => {
    if (isLoadingVolumeItems || isVolumeItemsError) return
    if (validateItemsMutation.isPending) return
    if (scanValue.trim() !== '') return

    if (unscannedRows.length > 0) {
      setConfirmPartialOpen(true)
      return
    }

    handleValidateVolume()
  }

  const handleConfirmPartial = () => {
    setConfirmPartialOpen(false)
    handleValidateVolume({ removeUnscanned: true })
  }

  const handleCancelPartial = () => {
    setConfirmPartialOpen(false)
    focusScanInput()
  }

  const handleClose = () => {
    if (validateItemsMutation.isPending) return
    onClose()
  }

  const isBusy = validateItemsMutation.isPending

  const actions = (
    <>
      <Button onClick={handleClose} color="secondary" disabled={isBusy}>
        Cancelar
      </Button>
      <Button
        onClick={handleValidateClick}
        variant="contained"
        disabled={
          isLoadingVolumeItems ||
          isVolumeItemsError ||
          isBusy ||
          scanValue.trim() !== ''
        }
        startIcon={
          isBusy ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {isBusy ? 'Validando...' : 'Validar'}
      </Button>
    </>
  )

  return (
    <>
      <BaseDialog
        open={open}
        onClose={handleClose}
        title={`Validar volume ${volume?.code ?? ''}${
          volume?.name ? ` - ${volume.name}` : ''
        }`}
        maxWidth="md"
        actions={actions}
      >
        <Paper
          variant="outlined"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 2,
            p: 2,
            mb: 2,
          }}
        >
          <SummaryItem
            label="Requisição"
            value={
              serviceOrder?.id
                ? `#${serviceOrder.id}`
                : movement?.service_order_id
                  ? `#${movement.service_order_id}`
                  : '-'
            }
          />
          <SummaryItem
            label="Itens da requisição"
            value={
              isLoadingMovement
                ? '...'
                : hasMovementSummary
                  ? requestedQuantity
                  : '-'
            }
          />
          <SummaryItem
            label="Pendentes na requisição"
            value={
              isLoadingMovement
                ? '...'
                : hasMovementSummary
                  ? pendingQuantity
                  : '-'
            }
          />
          <SummaryItem label="Situação">
            <Chip
              size="small"
              label={orderStatusLabel}
              color={orderStatusColor}
              sx={{ mt: 0.5, fontWeight: 600 }}
            />
          </SummaryItem>
        </Paper>

        {isMovementError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Não foi possível carregar os dados da requisição. A validação do
            volume pode continuar normalmente.
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Bipe novamente os equipamentos deste volume antes de validar. Ao
          validar sem bipar algum equipamento, ele permanecerá no movimento de
          origem e será agrupado no volume "Pendente de Carregamento - ID do
          carregamento" quando este carregamento for validado.
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Equipamentos do volume
          </Typography>
          <Chip
            size="small"
            label={
              equipmentRows.length === 0
                ? 'Nenhum equipamento'
                : `${scannedCount} de ${equipmentRows.length} bipados`
            }
            color={
              equipmentRows.length === 0 ||
              scannedCount === equipmentRows.length
                ? 'success'
                : 'warning'
            }
          />
        </Box>

        <TextField
          inputRef={scanInputRef}
          label="Bipar equipamento"
          placeholder="Escaneie ou digite o tombo"
          variant="outlined"
          size="small"
          value={scanValue}
          onChange={(event) => setScanValue(event.target.value)}
          onKeyDown={handleScanKeyDown}
          fullWidth
          disabled={
            isLoadingVolumeItems ||
            isVolumeItemsError ||
            isBusy ||
            confirmPartialOpen
          }
          helperText={
            scanValue.trim() !== ''
              ? 'Identificando equipamento...'
              : 'O equipamento precisa pertencer a este volume. A listagem é atualizada automaticamente.'
          }
          sx={{ mb: 2 }}
        />

        {isLoadingVolumeItems ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 3,
            }}
          >
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">
              Carregando equipamentos do volume...
            </Typography>
          </Box>
        ) : isVolumeItemsError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refetchVolumeItems}>
                Tentar novamente
              </Button>
            }
          >
            Erro ao buscar os equipamentos deste volume.
          </Alert>
        ) : equipmentRows.length === 0 ? (
          <Alert severity="info">
            Este volume não possui equipamentos para serem bipados.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 320 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Tombo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Equipamento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {equipmentRows.map((row) => {
                  const isScanned = scannedKeySet.has(row.key)

                  return (
                    <TableRow
                      key={row.key}
                      sx={{
                        backgroundColor: isScanned ? '#e8f5e9' : '#fff8e1',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.equipmentNumber}
                      </TableCell>
                      <TableCell>{row.equipmentName}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            icon={
                              isScanned ? (
                                <CheckCircleIcon />
                              ) : (
                                <RadioButtonUncheckedIcon />
                              )
                            }
                            label={isScanned ? 'Bipado' : 'Pendente'}
                            color={isScanned ? 'success' : 'warning'}
                            variant={isScanned ? 'filled' : 'outlined'}
                          />
                          {isScanned && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveScan(row.key)}
                              disabled={isBusy || confirmPartialOpen}
                              title="Desfazer bipagem"
                              aria-label={`Desfazer bipagem do equipamento ${row.equipmentNumber}`}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {productItems.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {productItemsQuantity}{' '}
            {productItemsQuantity === 1 ? 'insumo' : 'insumos'} deste volume{' '}
            {productItemsQuantity === 1
              ? 'será considerado'
              : 'serão considerados'}{' '}
            automaticamente.
          </Typography>
        )}
      </BaseDialog>

      <ConfirmationModal
        open={confirmPartialOpen}
        onClose={handleCancelPartial}
        onConfirm={handleConfirmPartial}
        text="Alguns itens não foram bipados. Estes itens permanecerão no movimento de origem e serão agrupados no volume 'Pendente de Carregamento - ID do carregamento' após a validação deste carregamento. Deseja seguir?"
      />
    </>
  )
}

export default VolumeValidationModal
